import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../../store/gameStore';
import { useSettingsStore } from '../../../store/settingsStore';
import { useTelegram } from '../../../hooks/useTelegram';
import { cn } from '../../../lib/cn';
import AIToast from '../../../components/AIToast';
import { getAIHint } from '../../../lib/groq';
import type { CellValue } from '../../../../../shared/types';

export default function GameControls() {
  const [aiHintMessage, setAIHintMessage] = useState<string | null>(null);
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  const { t, i18n } = useTranslation();
  const { haptic, showPopup, showConfirm } = useTelegram();
  const currentGame = useGameStore(state => state.currentGame);
  const isPaused = useGameStore(state => state.isPaused);
  const pauseGame = useGameStore(state => state.pauseGame);
  const resumeGame = useGameStore(state => state.resumeGame);
  const startNewGame = useGameStore(state => state.startNewGame);
  const restartCurrentGame = useGameStore(state => state.restartCurrentGame);
  const makeMove = useGameStore(state => state.makeMove);
  const setAIHintCell = useGameStore(state => state.setAIHintCell);
  const clearAIHintCell = useGameStore(state => state.clearAIHintCell);
  const toggleNoteMode = useGameStore(state => state.toggleNoteMode);
  const isNoteMode = useGameStore(state => state.isNoteMode);
  const language = useSettingsStore(state => state.language);

  if (!currentGame) return null;

  const handlePause = () => {
    haptic('light');
    if (isPaused) {
      resumeGame();
    } else {
      pauseGame();
    }
  };

  const handleRestart = async () => {
    if (currentGame && currentGame.status === 'in_progress') {
      const confirmed = await showConfirm(t('game.messages.confirmRestart'));
      if (!confirmed) return;
    }
    haptic('medium');
    restartCurrentGame();
  };

  const handleNewGame = async () => {
    if (currentGame && currentGame.status === 'in_progress') {
      const confirmed = await showConfirm(t('game.messages.confirmNewGame'));
      if (!confirmed) return;
    }
    haptic('medium');
    if (currentGame) {
      startNewGame(currentGame.difficulty);
    }
  };

  const handleNoteMode = () => {
    haptic('light');
    toggleNoteMode();
  };

  const handleHint = async () => {
    if (!currentGame) return;
    if (isLoadingHint) return;
    
    haptic('medium');
    setIsLoadingHint(true);
    clearAIHintCell();
    
    try {
      // Get AI hint - pass current language
      const hint = await getAIHint(currentGame.currentState, currentGame.solution, language || i18n.language);
      
      // Automatically insert the hint value into the game
      makeMove(hint.row, hint.col, hint.value as CellValue);
      
      // Show AI hint in grid (will stay until user sets value)
      setAIHintCell(hint.row, hint.col, hint.value);
      
      // Show typing animation toast
      setAIHintMessage(hint.explanation);
      
    } catch (error) {
      console.error('AI Hint error:', error);
      showPopup(t('game.hints.error'), t('game.hints.errorMessage'));
    } finally {
      setIsLoadingHint(false);
    }
  };

  return (
    <>
      {aiHintMessage && (
        <AIToast
          message={aiHintMessage}
          onClose={() => setAIHintMessage(null)}
        />
      )}
      
      <div className="space-y-3">
      {/* Main controls */}
      <div className="grid grid-cols-2 gap-2">
        <ControlButton
          onClick={handleNoteMode}
          disabled={currentGame.status === 'completed'}
          label={t('game.controls.notes')}
          iconType="notes"
          active={isNoteMode}
        />
        <ControlButton
          onClick={handleHint}
          disabled={currentGame.status === 'completed' || isLoadingHint}
          label={isLoadingHint ? '' : t('game.controls.hint')}
          iconType="ai"
        />
      </div>

      {/* Secondary controls */}
      <div className="grid grid-cols-3 gap-2">
        <ControlButton
          onClick={handlePause}
          disabled={currentGame.status === 'completed'}
          label={isPaused ? t('game.resume') : t('game.pause')}
          iconType={isPaused ? 'play' : 'pause'}
          variant="secondary"
        />
        <ControlButton
          onClick={handleRestart}
          disabled={currentGame.status === 'completed'}
          label={t('game.restart')}
          iconType="restart"
          variant="secondary"
        />
        <ControlButton
          onClick={handleNewGame}
          disabled={false}
          label={t('game.newGame')}
          iconType="refresh"
          variant="secondary"
        />
      </div>
    </div>
    </>
  );
}

interface ControlButtonProps {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  iconType: 'undo' | 'redo' | 'notes' | 'hint' | 'pause' | 'play' | 'flag' | 'refresh' | 'restart' | 'ai';
  active?: boolean;
  variant?: 'default' | 'secondary' | 'destructive';
}

const IconSVG = ({ type }: { type: ControlButtonProps['iconType'] }) => {
  const icons = {
    undo: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />,
    redo: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />,
    notes: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />,
    hint: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />,
    ai: (
      <>
        {/* AI Sparkles icon */}
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </>
    ),
    pause: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    play: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />,
    flag: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />,
    refresh: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
    restart: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
  };
  
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {icons[type]}
    </svg>
  );
};

function ControlButton({ 
  onClick, 
  disabled, 
  label, 
  iconType, 
  active,
  variant = 'default'
}: ControlButtonProps) {
  const isAIButton = iconType === 'ai';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative p-3 rounded-lg border transition-all',
        'flex flex-col items-center justify-center gap-1',
        'active:scale-95',
        isAIButton && !disabled && 'bg-gradient-to-br from-pink-400 to-rose-400 text-white border-pink-500 hover:from-pink-500 hover:to-rose-500',
        isAIButton && disabled && 'bg-gradient-to-br from-pink-300 to-rose-300 text-white/80 border-pink-400',
        !isAIButton && 'text-foreground',
        !isAIButton && variant === 'default' && 'border-border bg-card hover:bg-accent',
        !isAIButton && variant === 'secondary' && 'border-border bg-card hover:bg-secondary',
        !isAIButton && variant === 'destructive' && 'border-border bg-card hover:bg-destructive/10',
        !isAIButton && active && 'bg-primary text-primary-foreground border-primary',
        !isAIButton && disabled && 'opacity-30 cursor-not-allowed'
      )}
    >
      <IconSVG type={iconType} />
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}
